import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button } from './index';
import ProblemPreview from '../ProblemPreview';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const BulkLabelModal = ({
  isOpen,
  onClose,
  fetchWithTokenRefresh,
  onSuccess
}) => {
  const [targetPattern, setTargetPattern] = useState('');
  const [replacePattern, setReplacePattern] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProblems, setSelectedProblems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [previewProblemId, setPreviewProblemId] = useState(null);
  const [updateResult, setUpdateResult] = useState(null);
  
  // 인피니티 스크롤 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage] = useState(100);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef(null);

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setTargetPattern('');
      setReplacePattern('');
      setSearchResults([]);
      setSelectedProblems(new Set());
      setCurrentPage(1);
      setTotalPages(1);
      setTotalItems(0);
      setUpdateResult(null);
      setHasMore(false);
    }
  }, [isOpen]);

  // 라벨 패턴 검색
  const handleSearch = useCallback(async (page = 1, append = false) => {
    if (!targetPattern.trim()) {
      alert('검색할 라벨 패턴을 입력해주세요.');
      return;
    }

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setUpdateResult(null);
    }

    try {
      const url = `/api/problems/bulk-label?target_pattern=${encodeURIComponent(targetPattern.trim())}&page=${page}&per_page=${perPage}`;
      
      const response = await fetchWithTokenRefresh(url, { method: 'GET' });

      if (response?.status === 'success' && response?.data) {
        if (append) {
          // 인피니티 스크롤: 기존 데이터에 추가
          setSearchResults(prev => [...prev, ...(response.data || [])]);
          // 인피니티 스크롤로 추가된 데이터는 자동 선택하지 않음
          // 사용자가 명시적으로 전체 선택을 누르거나 개별 선택해야 함
        } else {
          // 새 검색: 기존 데이터 교체
          setSearchResults(response.data || []);
          // 검색 결과가 있으면 첫 100개만 선택
          if (response.data && response.data.length > 0) {
            const maxSelect = 100;
            const first100Problems = response.data.slice(0, maxSelect).map(p => p.problem_id);
            setSelectedProblems(new Set(first100Problems));
          } else {
            setSelectedProblems(new Set());
          }
        }
        
        setTotalItems(response.total_items || 0);
        setTotalPages(response.total_pages || 1);
        setCurrentPage(response.page || 1);
        setHasMore(response.page < response.total_pages);
      } else {
        if (!append) {
          setSearchResults([]);
          setTotalItems(0);
          setTotalPages(1);
          alert(response?.message || '검색 결과가 없습니다.');
        }
      }
    } catch (error) {
      console.error('라벨 검색 실패:', error);
      if (!append) {
        alert('라벨 검색에 실패했습니다.');
        setSearchResults([]);
        setTotalItems(0);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [targetPattern, perPage, fetchWithTokenRefresh]);

  // 인피니티 스크롤: 다음 페이지 로드
  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore && currentPage < totalPages) {
      handleSearch(currentPage + 1, true);
    }
  }, [isLoadingMore, hasMore, currentPage, totalPages, handleSearch]);

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !hasMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // 하단에서 100px 전에 로드
      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadMore]);

  // 라벨 일괄 수정 실행
  const handleBulkUpdate = useCallback(async () => {
    const problemIds = Array.from(selectedProblems);
    if (problemIds.length === 0) {
      alert('수정할 문제를 선택해주세요.');
      return;
    }

    if (!targetPattern.trim()) {
      alert('검색할 라벨 패턴을 입력해주세요.');
      return;
    }

    if (!replacePattern.trim() && replacePattern !== '') {
      // 빈 문자열로 교체하는 것도 허용
    }

    setIsUpdating(true);
    setUpdateResult(null);
    try {
      const response = await fetchWithTokenRefresh(
        '/api/problems/bulk-label',
        {
          method: 'POST',
          body: JSON.stringify({
            problem_ids: problemIds,
            target_pattern: targetPattern.trim(),
            replace_pattern: replacePattern
          })
        }
      );

      if (response?.status === 'success' && response?.data) {
        setUpdateResult(response.data);
        
        // 수정 성공 후 리스트 초기화
        setSearchResults([]);
        setSelectedProblems(new Set());
        setCurrentPage(1);
        setTotalPages(1);
        setTotalItems(0);
        setHasMore(false);
        
        // 성공 콜백 호출
        if (onSuccess) {
          onSuccess();
        }

        // 결과 메시지 생성
        let message = `라벨 일괄 수정 완료\n\n`;
        message += `요청: ${response.data.requested_count}개\n`;
        message += `성공: ${response.data.success_count}개\n`;
        message += `실패: ${response.data.fail_count}개`;
        
        if (response.data.problem_not_found?.length > 0) {
          message += `\n\n❌ 문제 없음: ${response.data.problem_not_found.length}개`;
        }
        if (response.data.target_pattern_not_found?.length > 0) {
          message += `\n⚠️ 패턴 없음: ${response.data.target_pattern_not_found.length}개`;
        }
        if (response.data.duplicate_pattern_found?.length > 0) {
          message += `\n⚠️ 중복 패턴: ${response.data.duplicate_pattern_found.length}개`;
        }
        if (response.data.label_too_long?.length > 0) {
          message += `\n❌ 라벨 길이 초과: ${response.data.label_too_long.length}개`;
        }

        alert(message);
      } else {
        alert(response?.message || '라벨 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('라벨 수정 실패:', error);
      alert('라벨 수정에 실패했습니다.');
    } finally {
      setIsUpdating(false);
    }
  }, [selectedProblems, targetPattern, replacePattern, fetchWithTokenRefresh, onSuccess]);

  // 문제 선택/해제 (최대 100개 제한)
  const toggleProblemSelection = (problemId) => {
    setSelectedProblems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(problemId)) {
        // 해제는 항상 가능
        newSet.delete(problemId);
      } else {
        // 선택 시 최대 100개 제한 확인
        if (newSet.size >= 100) {
          alert('최대 100개까지만 선택할 수 있습니다.');
          return prev;
        }
        newSet.add(problemId);
      }
      return newSet;
    });
  };

  // 전체 선택/해제 (최대 100개)
  const toggleSelectAll = () => {
    const maxSelect = 100;
    const first100Problems = searchResults.slice(0, maxSelect).map(p => p.problem_id);
    const isAllFirst100Selected = first100Problems.length > 0 && 
      first100Problems.every(id => selectedProblems.has(id));
    
    if (isAllFirst100Selected) {
      // Off: 선택 해제
      setSelectedProblems(new Set());
    } else {
      // On: 첫 100개 선택
      setSelectedProblems(new Set(first100Problems));
    }
  };

  // 날짜 포맷
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  // 검색 패턴 하이라이트 함수
  const highlightPattern = (text, pattern) => {
    if (!text || !pattern || pattern.trim() === '') {
      return text || '-';
    }

    const parts = text.split(pattern);
    const result = [];
    
    for (let i = 0; i < parts.length; i++) {
      if (parts[i]) {
        result.push(parts[i]);
      }
      if (i < parts.length - 1) {
        result.push(
          <span key={i} className="font-bold text-primary">
            {pattern}
          </span>
        );
      }
    }
    
    return result.length > 0 ? result : text;
  };

  return createPortal(
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="라벨 일괄 수정"
        size="xl"
        zIndex={9999950}
        footer={
          <div className="flex justify-between w-full">
            <div className="text-sm text-gray-600">
              {searchResults.length > 0 && (
                <>
                  로드: {searchResults.length}개 / 전체: {totalItems}개
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={onClose}
              >
                닫기
              </Button>
              <Button
                variant="primary"
                onClick={handleBulkUpdate}
                disabled={isUpdating || selectedProblems.size === 0 || !replacePattern.trim()}
              >
                {isUpdating ? '수정 중...' : `선택한 ${selectedProblems.size}개 수정`}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* 검색 패턴 입력 영역 */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="검색할 라벨 패턴 입력 (필수)"
                value={targetPattern}
                onChange={(e) => setTargetPattern(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 rounded-3xl border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none placeholder:text-sm"
              />
              <Button
                onClick={() => handleSearch(1)}
                disabled={isLoading}
                variant="primary"
              >
                {isLoading ? '검색 중...' : '검색'}
              </Button>
            </div>
            
            {/* 교체 패턴 입력 */}
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600 whitespace-nowrap">→</span>
              <input
                type="text"
                placeholder="교체할 라벨 패턴 입력 (필수)"
                value={replacePattern}
                onChange={(e) => setReplacePattern(e.target.value)}
                className="flex-1 rounded-3xl border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none placeholder:text-sm"
              />
            </div>
            
            <div className="text-xs text-gray-500">
              * 라벨에서 "
              <span className="font-bold text-primary">
                {targetPattern || '검색 패턴'}
              </span>
              "을 찾아 "
              <span className="font-bold text-primary">
                {replacePattern || '교체 패턴'}
              </span>
              "으로 변경합니다.
            </div>
          </div>

          {/* 수정 결과 메시지 */}
          {updateResult && (
            <div className="rounded-lg bg-blue-50 p-3 text-sm">
              <div className="font-semibold mb-2">수정 결과:</div>
              {updateResult.success_count > 0 && (
                <div className="text-green-600">
                  ✅ 성공: {updateResult.success_count}개
                </div>
              )}
              {updateResult.target_pattern_not_found?.length > 0 && (
                <div className="text-orange-600">
                  ⚠️ 패턴 없음: {updateResult.target_pattern_not_found.length}개
                </div>
              )}
              {updateResult.duplicate_pattern_found?.length > 0 && (
                <div className="text-orange-600">
                  ⚠️ 중복 패턴: {updateResult.duplicate_pattern_found.length}개
                </div>
              )}
              {updateResult.problem_not_found?.length > 0 && (
                <div className="text-red-600">
                  ❌ 문제 없음: {updateResult.problem_not_found.length}개
                </div>
              )}
              {updateResult.label_too_long?.length > 0 && (
                <div className="text-red-600">
                  ❌ 라벨 길이 초과: {updateResult.label_too_long.length}개
                </div>
              )}
            </div>
          )}

          {/* 검색 결과 테이블 */}
          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(() => {
                      const maxSelect = 100;
                      const first100Problems = searchResults.slice(0, maxSelect).map(p => p.problem_id);
                      return first100Problems.length > 0 && 
                        first100Problems.every(id => selectedProblems.has(id));
                    })()}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">전체 선택</span>
                </label>
                <span className="text-sm text-gray-600">
                  <span className="font-bold">{selectedProblems.size}개</span> 선택됨 <span className="font-bold text-primary">(최대 100개)</span>
                </span>
              </div>

              <div 
                ref={scrollContainerRef}
                className="max-h-[400px] overflow-y-auto overflow-x-auto"
              >
                <table className="w-full min-w-[600px]" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '51%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-100">
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">선택</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">문제 ID</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">라벨</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">상태</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">수정일</th>
                      <th className="px-2 md:px-4 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100">미리보기</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {searchResults.map((problem) => (
                      <tr key={problem.problem_id} className="hover:bg-gray-50">
                        <td className="px-2 md:px-4 py-2">
                          <input
                            type="checkbox"
                            checked={selectedProblems.has(problem.problem_id)}
                            onChange={() => toggleProblemSelection(problem.problem_id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-2 md:px-4 py-2 text-xs md:text-sm">{problem.problem_id}</td>
                        <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600">
                          <span 
                            title={problem.labels}
                            className="block truncate"
                          >
                            {highlightPattern(problem.labels, targetPattern.trim())}
                          </span>
                        </td>
                        <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600">
                          {problem.status === 999 ? '확정' : problem.status}
                        </td>
                        <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600">
                          {formatDate(problem.updated_at)}
                        </td>
                        <td className="px-2 md:px-4 py-2 text-center">
                          <button
                            onClick={() => setPreviewProblemId(problem.problem_id)}
                            className="text-blue-500 hover:text-blue-700 text-xs md:text-sm"
                          >
                            보기
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* 인피니티 스크롤 로딩 표시 */}
                {isLoadingMore && (
                  <div className="flex justify-center items-center py-4">
                    <div className="text-sm text-gray-500">로딩 중...</div>
                  </div>
                )}
                
                {/* 더 이상 로드할 데이터가 없을 때 */}
                {!hasMore && searchResults.length > 0 && (
                  <div className="flex justify-center items-center py-4">
                    <div className="text-sm text-gray-500">모든 데이터를 불러왔습니다.</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 검색 결과가 없을 때 */}
          {!isLoading && searchResults.length === 0 && targetPattern && (
            <div className="text-center py-8 text-gray-500">
              검색 결과가 없습니다.
            </div>
          )}
          
          {/* 초기 안내 메시지 */}
          {!isLoading && searchResults.length === 0 && !targetPattern && (
            <div className="text-center py-8 text-gray-500">
              라벨 패턴을 입력하고 검색 버튼을 눌러주세요.
            </div>
          )}
        </div>
      </Modal>
      
      {/* 문제 미리보기 모달 */}
      {previewProblemId && (
        <ProblemPreview
          isOpen={true}
          onClose={() => setPreviewProblemId(null)}
          problemIds={[previewProblemId]}
          showNumbers={false}
          correctList={[]}
        />
      )}
    </>,
    document.body
  );
};

export default BulkLabelModal;

